import { Api } from 'api';
import { ChartData } from 'chart.js';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-unresolved
import { Chart } from 'react-chartjs-2';
import { useMutation, useQuery } from 'react-query';
import {
  ApiResponse,
  ReportingQueryCacheKey,
  CampaignListReportResponse,
  CampaignUsedDetailReportResponse,
  CampaignReportItem,
} from 'types';
import {
  Button,
  Center,
  Icon,
  Text,
  useToast,
  Box,
  useColorModeValue,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { hideLoading, showLoading } from 'features';
import { downloadBlobAsFile } from 'core';
import { DownloadIcon, ViewIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'components';

export interface AdminReportCampaingsProps {
  // Campaign reports don't need date filtering based on the API
  groupType?: number;
}

type CampaignReportChartData = ChartData<'bar', number[], string>;
type CampaignReportData = {
  graphData: CampaignReportChartData;
  isEmpty: boolean;
  campaigns: CampaignReportItem[];
};

export const AdminReportCampaings: React.FC<AdminReportCampaingsProps> = ({ groupType }) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [selectedCampaignName, setSelectedCampaignName] = useState<string>('');
  const toast = useToast();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Simple color values for the table
  const tableHeaderBg = useColorModeValue('gray.700', 'gray.600');
  const tableHeaderText = useColorModeValue('white', 'white');

  // Fetch campaign list
  const { data: campaignListData, isLoading: isLoadingList } = useQuery(
    [ReportingQueryCacheKey.GetCampaignReport, 'list', groupType],
    async (): Promise<CampaignReportData | null> => {
      const response: ApiResponse<CampaignListReportResponse> = await Api.Reporting.getCampaignListReport(groupType);
      const queryData = response.data;

      if (!queryData.campaigns || queryData.campaigns.length === 0) {
        return {
          isEmpty: true,
          graphData: {
            labels: [],
            datasets: [],
          },
          campaigns: [],
        };
      }

      // Create chart data from campaigns
      const campaignNames = queryData.campaigns.map((campaign) =>
        campaign.name.length > 20 ? `${campaign.name.substring(0, 20)}...` : campaign.name,
      );
      const usageCounts = queryData.campaigns.map((campaign) => campaign.totalUsedCount);

      return {
        isEmpty: false,
        graphData: {
          labels: campaignNames,
          datasets: [
            {
              type: 'bar' as const,
              label: 'Kampanya Kullanım Sayısı',
              data: usageCounts,
              backgroundColor: 'rgb(75, 192, 192)',
              borderColor: 'white',
              borderWidth: 2,
            },
          ],
        },
        campaigns: queryData.campaigns,
      };
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  );

  // Fetch campaign usage details for modal
  const { data: campaignDetailData, isLoading: isLoadingDetail } = useQuery(
    [ReportingQueryCacheKey.GetCampaignReport, 'detail', selectedCampaignId],
    async (): Promise<CampaignUsedDetailReportResponse[] | null> => {
      if (!selectedCampaignId) return null;

      const response: ApiResponse<CampaignUsedDetailReportResponse[]> = await Api.Reporting.getCampaignUsedDetailReport(
        selectedCampaignId,
      );
      return response.data;
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!selectedCampaignId,
    },
  );

  // Export campaign list
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exportCampaignList = useMutation<any, any>(
    async () => {
      dispatch(showLoading());
      const response = await Api.Reporting.exportCampaignReport(groupType);
      if (!response) throw new Error('No response');
      return response;
    },
    {
      onSuccess(data) {
        if (data) {
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
          const fileName = `KampanyaListesi_${formattedDate}.xlsx`;
          downloadBlobAsFile(data, fileName);
        } else {
          toast({
            status: 'info',
            title: 'Rapor Bulunamadı',
            isClosable: true,
          });
        }
      },
      onSettled() {
        dispatch(hideLoading());
      },
      onError() {
        toast({
          status: 'error',
          title: 'Rapor İndirilirken Hata Oluştu',
          isClosable: true,
        });
      },
    },
  );

  // Export campaign usage details
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const exportCampaignDetails = useMutation<any, any>(
    async () => {
      if (!selectedCampaignId) return null;
      dispatch(showLoading());
      const response = await Api.Reporting.exportCampaignUsedDetailReport(selectedCampaignId);
      if (!response) throw new Error('No response');
      return response;
    },
    {
      onSuccess(data) {
        if (data) {
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
          const fileName = `KampanyaDetay_${selectedCampaignName}_${formattedDate}.xlsx`;
          downloadBlobAsFile(data, fileName);
        } else {
          toast({
            status: 'info',
            title: 'Rapor Bulunamadı',
            isClosable: true,
          });
        }
      },
      onSettled() {
        dispatch(hideLoading());
      },
      onError() {
        toast({
          status: 'error',
          title: 'Rapor İndirilirken Hata Oluştu',
          isClosable: true,
        });
      },
    },
  );

  const campaignColumnHelper = createColumnHelper<CampaignReportItem>();
  const detailColumnHelper = createColumnHelper<CampaignUsedDetailReportResponse>();

  const campaignColumns = [
    campaignColumnHelper.accessor('name', {
      header: 'Kampanya Adı',
      cell: (info) => info.getValue(),
    }),
    campaignColumnHelper.accessor('discountRateStr', {
      header: 'İndirim Oranı',
      cell: (info) => info.getValue(),
    }),
    campaignColumnHelper.accessor('endDate', {
      header: 'Bitiş Tarihi',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR'),
    }),
    campaignColumnHelper.accessor('totalUsedCount', {
      header: 'Kullanım Sayısı',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    campaignColumnHelper.accessor('totalAssignmentCount', {
      header: 'Toplam Atama',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    campaignColumnHelper.accessor('totalUnusedCount', {
      header: 'Kullanılmayan',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    campaignColumnHelper.display({
      id: 'actions',
      header: 'İşlemler',
      cell: (info) => (
        <Button
          size="sm"
          leftIcon={<Icon as={ViewIcon} />}
          onClick={() => {
            setSelectedCampaignId(info.row.original.id);
            setSelectedCampaignName(info.row.original.name);
            onOpen();
          }}
        >
          Detay
        </Button>
      ),
    }),
  ];

  const detailColumns = [
    detailColumnHelper.accessor('phoneNumber', {
      header: 'Telefon Numarası',
      cell: (info) => info.getValue(),
    }),

    detailColumnHelper.accessor('assignmentDate', {
      header: 'Atama Tarihi',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR'),
    }),
    detailColumnHelper.accessor('campaignCode', {
      header: 'Kampanya Kodu',
      cell: (info) => info.getValue(),
    }),
    detailColumnHelper.accessor('discountRateStr', {
      header: 'İndirim Oranı',
      cell: (info) => info.getValue(),
    }),
    detailColumnHelper.accessor('startDate', {
      header: 'Başlangıç',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR'),
    }),
    detailColumnHelper.accessor('endDate', {
      header: 'Bitiş',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR'),
    }),
    detailColumnHelper.accessor('assignmentCount', {
      header: 'Atanan Sayı',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    detailColumnHelper.accessor('usedByPhoneNumberCount', {
      header: 'Kullanım Sayısı',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    detailColumnHelper.display({
      id: 'unusedCount',
      header: 'Kullanılmayan',
      cell: (info) => {
        const row = info.row.original as any;
        return row.unusedCount || '-';
      },
      meta: {
        isNumeric: true,
      } as any,
    }),
  ];

  if (isLoadingList) {
    return (
      <Center>
        <VStack spacing={4}>
          <Spinner size="xl" />
          <Text>Kampanya verileri yükleniyor...</Text>
        </VStack>
      </Center>
    );
  }

  if (!campaignListData || campaignListData.isEmpty) {
    return (
      <Center>
        <Text fontSize="2xl" mt={2} mb={6}>
          Kampanya Verisi Bulunmuyor!
        </Text>
      </Center>
    );
  }

  return (
    <>
      <Button variant="solid" leftIcon={<Icon as={DownloadIcon} />} onClick={() => exportCampaignList.mutate()}>
        Excel İndir
      </Button>

      {campaignListData.campaigns && campaignListData.campaigns.length > 0 && (
        <Box mt={6} overflowX="auto">
          <DataTable
            data={campaignListData.campaigns}
            columns={campaignColumns}
            usePagination
            tableProps={{
              variant: 'simple',
              colorScheme: 'blue',
              size: 'sm',
              sx: {
                'thead tr': {
                  backgroundColor: tableHeaderBg,
                },
                'thead th': {
                  color: tableHeaderText,
                },
              },
            }}
          />
        </Box>
      )}

      <StatGroup p={4}>
        <Stat>
          <StatLabel>Toplam Kampanya Sayısı</StatLabel>
          <StatNumber>{campaignListData.campaigns.length}</StatNumber>
          <StatHelpText>Sistemdeki kampanyalar</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>Toplam Kullanım</StatLabel>
          <StatNumber>
            {campaignListData.campaigns.reduce((sum, campaign) => sum + campaign.totalUsedCount, 0)}
          </StatNumber>
          <StatHelpText>Tüm kampanyalar</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>Toplam Atama</StatLabel>
          <StatNumber>
            {campaignListData.campaigns.reduce((sum, campaign) => sum + campaign.totalAssignmentCount, 0)}
          </StatNumber>
          <StatHelpText>Atanan kampanyalar</StatHelpText>
        </Stat>
        <Stat>
          <StatLabel>Kullanılmayan</StatLabel>
          <StatNumber>
            {campaignListData.campaigns.reduce((sum, campaign) => sum + campaign.totalUnusedCount, 0)}
          </StatNumber>
          <StatHelpText>Bekleyen kampanyalar</StatHelpText>
        </Stat>
      </StatGroup>

      <Chart
        type="bar"
        data={campaignListData.graphData}
        options={{
          responsive: true,
          scales: {
            x: {
              type: 'category',
              title: {
                display: true,
                text: 'Kampanyalar',
              },
            },
            y: {
              type: 'linear',
              position: 'left',
              title: {
                display: true,
                text: 'Kullanım Sayısı',
              },
            },
          },
        }}
      />

      {/* Campaign Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack justify="space-between">
              <Text>Kampanya Detayları: {selectedCampaignName}</Text>
              <Button
                variant="solid"
                leftIcon={<Icon as={DownloadIcon} />}
                onClick={() => exportCampaignDetails.mutate()}
                size="sm"
              >
                Detay Excel İndir
              </Button>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoadingDetail && (
              <Center py={8}>
                <VStack spacing={4}>
                  <Spinner />
                  <Text>Kampanya detayları yükleniyor...</Text>
                </VStack>
              </Center>
            )}

            {campaignDetailData && campaignDetailData.length > 0 && (
              <>
                <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={6}>
                  <Stat>
                    <StatLabel>Toplam Telefon Numarası</StatLabel>
                    <StatNumber>{campaignDetailData.length}</StatNumber>
                    <StatHelpText>Kampanya kullanan numaralar</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Toplam Kullanım</StatLabel>
                    <StatNumber>
                      {campaignDetailData.reduce((sum, item) => sum + item.usedByPhoneNumberCount, 0)}
                    </StatNumber>
                    <StatHelpText>Tüm numaralar toplamı</StatHelpText>
                  </Stat>
                  <Stat>
                    <StatLabel>Ortalama Kullanım</StatLabel>
                    <StatNumber>
                      {Math.round(
                        campaignDetailData.reduce((sum, item) => sum + item.usedByPhoneNumberCount, 0) /
                          campaignDetailData.length,
                      )}
                    </StatNumber>
                    <StatHelpText>Numara başına</StatHelpText>
                  </Stat>
                </SimpleGrid>

                <Box overflowX="auto">
                  <DataTable
                    data={campaignDetailData}
                    columns={detailColumns}
                    usePagination
                    tableProps={{
                      variant: 'simple',
                      colorScheme: 'blue',
                      size: 'sm',
                      sx: {
                        'thead tr': {
                          backgroundColor: tableHeaderBg,
                        },
                        'thead th': {
                          color: tableHeaderText,
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}

            {campaignDetailData && campaignDetailData.length === 0 && (
              <Center py={8}>
                <Text fontSize="lg" color="gray.500">
                  Seçilen kampanya için kullanım verisi bulunmuyor.
                </Text>
              </Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Kapat</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
